/**
 * transform-countdown block
 *
 * An alternating-card section (matching the C1 Transform page: full-bleed,
 * #DCDADA rails, sharp corners, 600px min-height): a light-teal placeholder
 * panel on the left and a live "days remaining" countdown on the right.
 *
 * The count is computed on the client (the page is edge-cached via ISR, so a
 * server-rendered number would freeze at cache time). It renders an initial
 * value for first paint, then corrects on hydration and refreshes hourly.
 */
import { useEffect, useState } from "react";
import { DeferredAnimationFrame } from "~/components/DeferredAnimationFrame";

function daysUntil(target: Date): number {
  const now = new Date();
  const ms = target.getTime() - now.getTime();
  return Math.max(0, Math.ceil(ms / 86_400_000));
}

export function TransformCountdown({
  target_date = "2026-10-05",
  label = "days until C1 Transform",
  sublabel,
  left_bg = "#8AF1D9",
  left_embed,
  right_bg,
  card_min_height = 600,
}: {
  /** Countdown target, YYYY-MM-DD (interpreted as local midnight). */
  target_date?: string;
  /** Line under the big number. */
  label?: string;
  /** Optional smaller line under the label (e.g. date + location). */
  sublabel?: string;
  /** Background of the left placeholder panel. */
  left_bg?: string;
  /** URL of a self-contained HTML page to embed in the left card (iframe,
   *  fills the cell; given a defined aspect below lg so it doesn't collapse). */
  left_embed?: string;
  /** Background of the right countdown cell. When set (a dark color), the
   *  countdown text switches to white. Defaults to the section's white. */
  right_bg?: string;
  /** Card height in px. */
  card_min_height?: number;
}) {
  const dark = Boolean(right_bg);
  const [yy, mm, dd] = target_date.split("-").map(Number);
  const target = new Date(yy || 2026, (mm || 1) - 1, dd || 1);

  const [days, setDays] = useState(() => daysUntil(target));
  useEffect(() => {
    setDays(daysUntil(target));
    const id = setInterval(() => setDays(daysUntil(target)), 60 * 60 * 1000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target_date]);

  return (
    <section className="w-full bg-white">
      <div
        className="grid overflow-hidden lg:grid-cols-2 min-h-[var(--card-min-h)]"
        style={{ ["--card-min-h" as string]: `${card_min_height}px` }}
      >
        {/* Left: embedded animation, or a plain color panel. */}
        <div
          className={
            left_embed ? "flex aspect-[855/600] lg:aspect-[auto]" : undefined
          }
          style={{ backgroundColor: left_bg }}
        >
          {left_embed && (
            <DeferredAnimationFrame
              src={left_embed}
              title="C1 Transform animation"
              loading="lazy"
              className="h-full w-full border-0"
            />
          )}
        </div>

        {/* Right: countdown */}
        <div
          className="flex flex-col justify-center p-8 md:p-[32px]"
          style={right_bg ? { backgroundColor: right_bg } : undefined}
        >
          <span
            className={`block font-display font-semibold leading-none tabular-nums ${
              dark ? "text-white" : "text-charcoal"
            }`}
            style={{ fontSize: "min(150px, 15vw)" }}
            suppressHydrationWarning
          >
            {days}
          </span>
          {label && (
            <span
              className={`mt-2.5 block font-body text-33 font-semibold leading-tight lg:text-40 ${
                dark ? "text-white" : "text-charcoal"
              }`}
            >
              {label}
            </span>
          )}
          {sublabel && (
            <span
              className={`mt-2 block font-body text-19 ${
                dark ? "text-white/70" : "text-gray-80"
              }`}
            >
              {sublabel}
            </span>
          )}
        </div>
      </div>
    </section>
  );
}
